import { Box, Button, Flex, HStack, Text } from "@chakra-ui/react";
import { ListPageHeader } from "../common/components/ListPageHeader";
import { Tooltip } from "../common/components/Tooltip";
import { debounce } from "lodash";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import {
  ContractDatePresetDto,
  ContractNature,
  ListBuyerEventsApiArg,
  SavedReportListDto,
  SortDirection,
  SourcingEventSortField,
  useListBuyerEventFilterOptionsQuery,
  useListBuyerEventsQuery,
} from "../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../common/auth/useLoggedInWithOrgContextState";
import { CopiedFromLine } from "./sourcing-events/CopiedFromLine";
import { UnansweredQuestionsBadge } from "../common/components/communication/UnansweredQuestionsBadge";
import {
  EntityTable,
  EntityTableColumn,
} from "../common/entity-table/EntityTable";
import { SEARCH_MAX_LENGTH } from "../common/entity-table/columnFilters";
import { withLockedColumnsShown } from "../common/entity-table/columnsWithActiveFilter";
import { useColumnMemory } from "../common/entity-table/useColumnMemory";
import { useDisplayDate } from "../common/formatting/useDisplayDate";
import { useDisplayDateWithTime } from "../common/formatting/useDisplayDateWithTime";
import { SelectorValue } from "../common/input/Selector/SelectorValue";
import {
  BUCKET_META,
  MENU_PAGE_PARAM,
  STATUSES_BY_BUCKET,
  STATUS_FILTER_PARAM,
  isBucketKey,
  type BucketKey,
} from "../common/layout/statusBuckets";
import { useNavLabel } from "../common/layout/useNavLabel";
import { useRunsPublicProcurements } from "../common/organization/useRunsPublicProcurements";
import { useProcurementTerms } from "../common/terminology/useProcurementTerms";
import { displayPersonName } from "../common/persons/displayPersonName";
import { describeColumnFilters } from "../common/download/describeFilters";
import { getContractNatureLabel, getProcedureLabel } from "./sourcing-events/portal/utils/displayFormatters";
import {
  procedureText,
  regulationBucketText,
  regulationText,
} from "./sourcing-events/procurementClassificationText";
import { useEventNavigation } from "../common/sourcing-events/useEventNavigation";
import { EventListItemStatusTag } from "./sourcing-event/EventListItemStatusTag";
import { ProcurementListDownloadMenu } from "./sourcing-events/ProcurementListDownloadMenu";
import { useListReports } from "../common/saved-reports/useListReports";
import { FilterBar } from "../common/entity-table/FilterBar";
import { datePresetChoices } from "../common/entity-table/datePresetChoices";
import {
  OVERVIEW_FILTER_PARAMS,
  overviewFigureHeading,
  overviewFilterQuery,
  procurementOverviewFilterChips,
} from "./sourcing-events/procurementOverviewFilters";
import { LIST_FIGURE_PARAM } from "./overview/procurementFigures";
import { SearchHighlight } from "../common/entity-table/SearchHighlight";
import { TableSearchNoMatch } from "../common/entity-table/TableSearchNoMatch";
import { procurementSearchHitOutsideShownColumns } from "./sourcing-events/procurementSearchHit";

const PAGE_SIZE = 10;
const DEADLINE_PARAMS = ["deadlineFrom", "deadlineTo", "deadlinePreset"];
const DEADLINE_PRESETS = new Set<string>(Object.values(ContractDatePresetDto));
const SEARCH_PARAM = "tableSearchTerm";
const TITLE_SEARCH_PARAM = "name";

const bucketsIn = (value: string | null): BucketKey[] =>
  (value ?? "").split(",").filter(isBucketKey);

const statusesInAddress = (params: URLSearchParams): { menuBucket?: BucketKey; filterBuckets: BucketKey[] } => {
  if (params.has(STATUS_FILTER_PARAM)) return { filterBuckets: bucketsIn(params.get(STATUS_FILTER_PARAM)) };
  const legacyOrMenu = bucketsIn(params.get(MENU_PAGE_PARAM));
  return legacyOrMenu.length === 1 ? { menuBucket: legacyOrMenu[0], filterBuckets: [] } : { filterBuckets: legacyOrMenu };
};
const CONTRACT_NATURES: ContractNature[] = [
  ContractNature.Works,
  ContractNature.Supplies,
  ContractNature.Services,
  ContractNature.Combined,
];
const BASE_COLUMNS = [
  "title",
  "status",
  "responsiblePersons",
  "contractType",
  "deadline",
  "modifiedAt",
];
const ANNOUNCED_COLUMNS = [
  "title",
  "status",
  "responsiblePersons",
  "deadline",
  "procedure",
  "regulation",
  "modifiedAt",
];

const byLabel = (options: SelectorValue[]): SelectorValue[] =>
  [...options].sort((a, b) => a.label.localeCompare(b.label, "nb"));

const comparableReportQuery = (query: string): string => {
  const params = new URLSearchParams(query);
  const { menuBucket, filterBuckets } = statusesInAddress(params);
  params.delete(MENU_PAGE_PARAM);
  params.delete(STATUS_FILTER_PARAM);
  if (menuBucket) params.set(MENU_PAGE_PARAM, menuBucket);
  if (filterBuckets.length) params.set(STATUS_FILTER_PARAM, filterBuckets.join(","));
  return params.toString();
};

export const SourcingEventsList = () => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const displayDateWithTime = useDisplayDateWithTime();
  const navLabel = useNavLabel();
  const terms = useProcurementTerms();
  const runsPublicProcurements = useRunsPublicProcurements();
  const authState = useLoggedInWithOrgContextState();
  const navigateToEvent = useEventNavigation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [resetKey, setResetKey] = useState(0);
  const [personSearchTerm, setPersonSearchTerm] = useState("");

  const searchTerm = (searchParams.get(SEARCH_PARAM) ?? searchParams.get(TITLE_SEARCH_PARAM) ?? "").slice(
    0,
    SEARCH_MAX_LENGTH,
  );
  const searchedTerm = searchTerm.trim();
  const [searchInput, setSearchInput] = useState(searchTerm);
  const [searchTermInInput, setSearchTermInInput] = useState(searchTerm);
  if (searchTerm !== searchTermInInput) {
    setSearchTermInInput(searchTerm);
    setSearchInput(searchTerm);
  }
  const deadlinePassed = searchParams.get("deadline") === "passed";
  const { menuBucket, filterBuckets } = statusesInAddress(searchParams);
  const responsiblePersons = (searchParams.get("responsible") ?? "")
    .split(",")
    .filter(Boolean);
  const contractTypes = (searchParams.get("contractType") ?? "")
    .split(",")
    .filter(Boolean) as ContractNature[];
  const sortBy =
    (searchParams.get("sortBy") as SourcingEventSortField | null) ?? undefined;
  const sortDirection =
    (searchParams.get("sortDirection") as SortDirection | null) ?? undefined;
  const skip = Number(searchParams.get("skip") ?? 0);
  const overviewFilters = overviewFilterQuery(searchParams);
  const deadlineFrom = searchParams.get("deadlineFrom") || undefined;
  const deadlineTo = searchParams.get("deadlineTo") || undefined;
  const presetInAddress = searchParams.get("deadlinePreset") ?? "";
  const deadlinePreset = DEADLINE_PRESETS.has(presetInAddress) ? (presetInAddress as ContractDatePresetDto) : undefined;
  const hasDeadlineFilter = !!(deadlineFrom || deadlineTo || deadlinePreset);
  const procedureTypes = overviewFilters.procedureTypes ?? [];
  const regulationBuckets = overviewFilters.regulationBuckets ?? [];
  const hasOverviewFilters = OVERVIEW_FILTER_PARAMS.some((key) => searchParams.has(key));

  const updateParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          mutate(params);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setFilterParam = useCallback(
    (key: string, value: string | undefined, displaces: string[] = []) =>
      updateParams((params) => {
        displaces.forEach((other) => params.delete(other));
        if (value) params.set(key, value);
        else params.delete(key);
        params.set("skip", "0");
      }),
    [updateParams],
  );

  const writeSearch = useCallback(
    (value: string) =>
      updateParams((params) => {
        params.delete(TITLE_SEARCH_PARAM);
        if (value.trim()) params.set(SEARCH_PARAM, value);
        else params.delete(SEARCH_PARAM);
        params.set("skip", "0");
      }),
    [updateParams],
  );
  const debouncedWriteSearch = useMemo(() => debounce(writeSearch, 300), [writeSearch]);
  const forgetSearchInput = useCallback(() => {
    debouncedWriteSearch.cancel();
    setSearchInput("");
  }, [debouncedWriteSearch]);
  const debouncedPersonSearch = useMemo(
    () => debounce((value: string) => setPersonSearchTerm(value), 300),
    [],
  );

  const handleSort = useCallback(
    (column: SourcingEventSortField) => (direction: SortDirection) => {
      updateParams((params) => {
        if (direction) {
          params.set("sortBy", column);
          params.set("sortDirection", direction);
        } else {
          params.delete("sortBy");
          params.delete("sortDirection");
        }
        params.set("skip", "0");
      });
    },
    [updateParams],
  );

  const columnMemory = useColumnMemory("v2-procurements");

  const handleReset = useCallback(() => {
    forgetSearchInput();
    columnMemory.forget();
    setSearchParams(new URLSearchParams(), { replace: true });
    setPersonSearchTerm("");
    setResetKey((key) => key + 1);
  }, [columnMemory, setSearchParams, forgetSearchInput]);

  const defaultColumns = useMemo(() => {
    if (!runsPublicProcurements)
      return ["title", "internalReference", ...BASE_COLUMNS.slice(1)];
    return menuBucket === "kunngjort" ? ANNOUNCED_COLUMNS : BASE_COLUMNS;
  }, [runsPublicProcurements, menuBucket]);

  const visibleColumns = useMemo(() => {
    const value = searchParams.get("columns");
    if (!value || !value.trim())
      return new Set<string>(columnMemory.recall() ?? defaultColumns);
    return new Set(
      value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    );
  }, [searchParams, defaultColumns, columnMemory]);

  const setVisibleColumns = useCallback(
    (list: string[]) => {
      columnMemory.remember(list);
      updateParams((params) => params.set("columns", list.join(",")));
    },
    [columnMemory, updateParams],
  );
  const resetColumns = useCallback(() => {
    columnMemory.forget();
    updateParams((params) => params.delete("columns"));
  }, [columnMemory, updateParams]);
  const onShowColumn = useCallback(
    (key: string) => {
      if (!visibleColumns.has(key)) setVisibleColumns([...visibleColumns, key]);
    },
    [visibleColumns, setVisibleColumns],
  );
  const onHideColumn = useCallback(
    (key: string) =>
      setVisibleColumns([...visibleColumns].filter((k) => k !== key)),
    [visibleColumns, setVisibleColumns],
  );
  const hasColumnChanges = useMemo(
    () =>
      visibleColumns.size !== defaultColumns.length ||
      defaultColumns.some((k) => !visibleColumns.has(k)),
    [visibleColumns, defaultColumns],
  );

  // Context-aware filter options: only responsible persons, contract types and statuses that actually
  // exist in the org's events, so no filter offers a value that would return nothing.
  const {
    data: filterOptions,
    isLoading: filterOptionsLoading,
    isError: filterOptionsFailed,
    refetch: refetchFilterOptions,
  } = useListBuyerEventFilterOptionsQuery({
    orgId: authState.selectedOrg.id,
  });
  const filterOptionsState = {
    isLoading: filterOptionsLoading,
    isError: filterOptionsFailed,
    onRetry: () => void refetchFilterOptions(),
  };
  const responsiblePersonOptions = filterOptions?.responsiblePersons ?? [];
  const filteredResponsibleOptions = personSearchTerm
    ? responsiblePersonOptions.filter((p) =>
        displayPersonName(p)
          .toLowerCase()
          .includes(personSearchTerm.toLowerCase()),
      )
    : responsiblePersonOptions;

  // Selected chips are driven by the URL (source of truth); labels resolved from the complete set.
  const selectedResponsibleValues: SelectorValue[] = responsiblePersons.map(
    (id) => {
      const person = responsiblePersonOptions.find((p) => p.id === id);
      return { value: id, label: person ? displayPersonName(person) : id };
    },
  );

  const presentContractNatures = new Set(filterOptions?.contractNatures ?? []);
  const presentStatuses = new Set<string>(filterOptions?.statuses ?? []);
  const presentBuckets = filterOptions
    ? (Object.keys(BUCKET_META) as BucketKey[]).filter((bucket) =>
        STATUSES_BY_BUCKET[bucket].some((s) => presentStatuses.has(s))
      )
    : (Object.keys(BUCKET_META) as BucketKey[]);

  const overviewHeading = overviewFigureHeading(t, searchParams);
  const isPastDeadlinePage = !overviewHeading && deadlinePassed;
  const isMenuBucketPage = !overviewHeading && !!menuBucket;
  const statusBuckets = filterBuckets.length ? filterBuckets : menuBucket ? [menuBucket] : [];
  const chosenStatusBuckets = isMenuBucketPage ? [] : statusBuckets;
  const selectedStatuses = statusBuckets.flatMap((bucket) => STATUSES_BY_BUCKET[bucket]);
  const menuPageStatuses = isMenuBucketPage ? STATUSES_BY_BUCKET[menuBucket] : [];

  const queryArg: ListBuyerEventsApiArg = {
    orgId: authState.selectedOrg.id,
    tableSearchTerm: searchedTerm || undefined,
    statuses: selectedStatuses.length ? selectedStatuses : undefined,
    responsiblePersons: responsiblePersons.length
      ? responsiblePersons
      : undefined,
    contractTypes: contractTypes.length ? contractTypes : undefined,
    deadlinePassed: deadlinePassed || undefined,
    ...overviewFilters,
    deadlineFrom,
    deadlineTo,
    deadlinePreset,
    limit: PAGE_SIZE,
    skip,
  };
  if (sortBy) queryArg.sortBy = sortBy;
  if (sortDirection) queryArg.sortDirection = sortDirection;

  const overviewChips = procurementOverviewFilterChips(t, searchParams);
  const hasFilterChips =
    overviewChips.length > 0 ||
    !!searchedTerm ||
    responsiblePersons.length > 0 ||
    contractTypes.length > 0 ||
    hasDeadlineFilter ||
    procedureTypes.length > 0 ||
    regulationBuckets.length > 0 ||
    chosenStatusBuckets.length > 0;
  const { currentData: unfiltered } = useListBuyerEventsQuery(
    {
      orgId: authState.selectedOrg.id,
      statuses: menuPageStatuses.length ? menuPageStatuses : undefined,
      deadlinePassed: isPastDeadlinePage || undefined,
      limit: 1,
      skip: 0,
    },
    { skip: !hasFilterChips },
  );
  const { data, currentData, isFetching, isError } = useListBuyerEventsQuery(queryArg);

  const lastLoadedPage = useRef<typeof data>(undefined);
  if (data) lastLoadedPage.current = data;
  const page = data ?? lastLoadedPage.current;
  const events = page?.events ?? [];
  const count = page?.count ?? 0;

  const hasActiveFilters =
    !!searchedTerm ||
    statusBuckets.length > 0 ||
    responsiblePersons.length > 0 ||
    contractTypes.length > 0 ||
    deadlinePassed ||
    hasDeadlineFilter ||
    hasOverviewFilters ||
    !!sortBy ||
    skip > 0;

  const heading =
    overviewHeading ??
    (deadlinePassed
      ? t("Past their deadline")
      : isMenuBucketPage
        ? navLabel(BUCKET_META[menuBucket].labelKey)
        : navLabel("All procurements"));
  const showsWholeList = !isMenuBucketPage && !deadlinePassed && overviewChips.length === 0;

  const statusOptions: SelectorValue[] = presentBuckets.map((bucket) => ({
    value: bucket,
    label: navLabel(BUCKET_META[bucket].labelKey),
  }));

  const defaultFilterColumnKeys = isMenuBucketPage ? ["status"] : [];
  const unlockedColumns: EntityTableColumn[] = [
    {
      key: "title",
      label: t("Title"),
      onSort: handleSort(SourcingEventSortField.Title),
      currentSortDirection:
        sortBy === SourcingEventSortField.Title ? sortDirection : undefined,
      hidden: !visibleColumns.has("title"),
      alwaysShown: true,
    },
    {
      key: "internalReference",
      label: t("Internal reference"),
      onSort: handleSort(SourcingEventSortField.ReferenceNumber),
      currentSortDirection:
        sortBy === SourcingEventSortField.ReferenceNumber
          ? sortDirection
          : undefined,
      hidden: !visibleColumns.has("internalReference"),
    },
    {
      key: "status",
      label: t("Status"),
      hidden: !visibleColumns.has("status"),
      onSort: handleSort(SourcingEventSortField.Status),
      currentSortDirection:
        sortBy === SourcingEventSortField.Status ? sortDirection : undefined,
      multiSelectConfig: {
        options: statusOptions,
        selectedValues: statusBuckets.map((bucket) => ({
          value: bucket,
          label: navLabel(BUCKET_META[bucket].labelKey),
        })),
        onSelect: (values) =>
          updateParams((params) => {
            const chosen = values.map((v) => v.value).join(",");
            params.delete(MENU_PAGE_PARAM);
            if (chosen) params.set(STATUS_FILTER_PARAM, chosen);
            else params.delete(STATUS_FILTER_PARAM);
            params.set("skip", "0");
          }),
      },
    },
    {
      key: "responsiblePersons",
      label: t("Responsible person"),
      hidden: !visibleColumns.has("responsiblePersons"),
      onSort: handleSort(SourcingEventSortField.Responsible),
      currentSortDirection:
        sortBy === SourcingEventSortField.Responsible ? sortDirection : undefined,
      multiSelectConfig: {
        options: filteredResponsibleOptions.map((p) => ({
          value: p.id,
          label: displayPersonName(p),
        })),
        selectedValues: selectedResponsibleValues,
        onSelect: (values) =>
          setFilterParam("responsible", values.map((v) => v.value).join(",")),
        onInputChange: (value) => debouncedPersonSearch(value),
      },
    },
    {
      key: "contractType",
      label: t("Contract type"),
      hidden: !visibleColumns.has("contractType"),
      onSort: handleSort(SourcingEventSortField.ContractType),
      currentSortDirection:
        sortBy === SourcingEventSortField.ContractType ? sortDirection : undefined,
      multiSelectConfig: {
        options: CONTRACT_NATURES.filter((nature) =>
          presentContractNatures.has(nature),
        ).map((nature) => ({
          value: nature,
          label: getContractNatureLabel(nature, t),
        })),
        selectedValues: contractTypes.map((nature) => ({
          value: nature,
          label: getContractNatureLabel(nature, t),
        })),
        onSelect: (values) =>
          setFilterParam("contractType", values.map((v) => v.value).join(",")),
      },
    },
    {
      key: "deadline",
      label: t("Deadline"),
      onSort: handleSort(SourcingEventSortField.Deadline),
      currentSortDirection:
        sortBy === SourcingEventSortField.Deadline ? sortDirection : undefined,
      hidden: !visibleColumns.has("deadline"),
      isFilterActive: hasDeadlineFilter,
      dateRangeConfig: {
        fromDate: deadlineFrom,
        toDate: deadlineTo,
        fromDateLabel: String(t("From")),
        toDateLabel: String(t("To")),
        onFromDateChange: (date?: string) => setFilterParam("deadlineFrom", date, ["deadlinePreset"]),
        onToDateChange: (date?: string) => setFilterParam("deadlineTo", date, ["deadlinePreset"]),
        presets: datePresetChoices(t),
        preset: deadlinePreset,
        onPresetChange: (chosen?: string) =>
          setFilterParam("deadlinePreset", chosen, ["deadlineFrom", "deadlineTo"]),
      },
    },
    {
      key: "procedure",
      label: t("Procedure"),
      onSort: handleSort(SourcingEventSortField.Procedure),
      currentSortDirection:
        sortBy === SourcingEventSortField.Procedure ? sortDirection : undefined,
      hidden: !visibleColumns.has("procedure"),
      multiSelectConfig: {
        options: byLabel(
          (filterOptions?.procedureTypes ?? []).map((type) => ({ value: type, label: getProcedureLabel(type, t) })),
        ),
        selectedValues: procedureTypes.map((type) => ({ value: type, label: getProcedureLabel(type, t) })),
        onSelect: (values) =>
          setFilterParam("procedureTypes", values.map((v) => v.value).join(","), [LIST_FIGURE_PARAM]),
        ...filterOptionsState,
      },
    },
    {
      key: "regulation",
      label: t("Regulation"),
      onSort: handleSort(SourcingEventSortField.Regulation),
      currentSortDirection:
        sortBy === SourcingEventSortField.Regulation ? sortDirection : undefined,
      hidden: !visibleColumns.has("regulation"),
      multiSelectConfig: {
        options: byLabel(
          (filterOptions?.regulationBuckets ?? []).map((bucket) => ({
            value: bucket,
            label: regulationBucketText(bucket, t),
          })),
        ),
        selectedValues: regulationBuckets.map((bucket) => ({ value: bucket, label: regulationBucketText(bucket, t) })),
        onSelect: (values) =>
          setFilterParam("regulationBuckets", values.map((v) => v.value).join(","), [LIST_FIGURE_PARAM]),
        ...filterOptionsState,
      },
    },
    {
      key: "modifiedAt",
      label: t("Last modified"),
      onSort: handleSort(SourcingEventSortField.ModifiedAt),
      currentSortDirection:
        sortBy === SourcingEventSortField.ModifiedAt
          ? sortDirection
          : undefined,
      hidden: !visibleColumns.has("modifiedAt"),
    },
  ];
  const columns = withLockedColumnsShown(unlockedColumns, defaultFilterColumnKeys);

  const clearOverviewFilters = (keys: string[]) =>
    updateParams((params) => {
      keys.forEach((key) => params.delete(key));
      params.delete(LIST_FIGURE_PARAM);
      params.set("skip", "0");
    });
  const clearSearch = () => {
    forgetSearchInput();
    writeSearch("");
  };
  const handleResetAllFilters = () => {
    forgetSearchInput();
    updateParams((params) => {
      [
        SEARCH_PARAM,
        TITLE_SEARCH_PARAM,
        "responsible",
        "contractType",
        LIST_FIGURE_PARAM,
        ...OVERVIEW_FILTER_PARAMS,
        ...DEADLINE_PARAMS,
      ].forEach((key) => params.delete(key));
      params.delete(STATUS_FILTER_PARAM);
      if (!isMenuBucketPage) params.delete(MENU_PAGE_PARAM);
      if (!isPastDeadlinePage) params.delete("deadline");
      params.set("skip", "0");
    });
  };
  const countText =
    !currentData || !unfiltered || isError
      ? null
      : searchedTerm
        ? t("{{shown}} of {{count}} procurements with «{{term}}»", {
            shown: currentData.count,
            count: unfiltered.count,
            term: searchedTerm,
          })
        : t("{{shown}} of {{count}} procurements", { shown: currentData.count, count: unfiltered.count });
  const activeFilterDescriptions = [
    ...overviewChips.map((chip) => chip.label),
    ...describeColumnFilters(
      t,
      columns.filter((column) => !defaultFilterColumnKeys.includes(column.key)),
    ),
  ];
  const shownColumnKeys = new Set(columns.filter((column) => !column.hidden).map((column) => column.key));
  const columnLabel = (key: string) => columns.find((column) => column.key === key)?.label ?? key;

  const reportQuery = new URLSearchParams(searchParams);
  reportQuery.delete("skip");
  reportQuery.set("columns", columns.filter((column) => !column.hidden).map((column) => column.key).join(","));

  const rows = events.map((event) => {
    const hiddenHit = procurementSearchHitOutsideShownColumns(event, searchedTerm, shownColumnKeys);
    return [
    <Box key="title" minW="0" maxW="80">
      <HStack spacing="2" minW="0">
        <Tooltip label={event.title || t("Untitled")}>
          <Button
            variant="link"
            color="smText"
            fontWeight="medium"
            textAlign="left"
            minW="0"
            display="block"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            onClick={() => navigateToEvent(event)}
          >
            {event.title ? <SearchHighlight text={event.title} term={searchedTerm} /> : t("Untitled")}
          </Button>
        </Tooltip>
        <UnansweredQuestionsBadge count={event.unansweredQuestionsCount} />
      </HStack>
      {hiddenHit && (
        <Text fontSize="xs" color="smMuted" noOfLines={1} data-testid="hidden-search-hit">
          {t("Match in {{column}}:", { column: columnLabel(hiddenHit.field) })}{" "}
          <SearchHighlight text={hiddenHit.value} term={searchedTerm} />
        </Text>
      )}
      <CopiedFromLine event={event} />
    </Box>,
    <Text key="internalReference" color="smMuted" textAlign="right">
      {event.referenceId && <SearchHighlight text={event.referenceId} term={searchedTerm} />}
    </Text>,
    <EventListItemStatusTag key="status" event={event} size="sm" />,
    <Text key="responsible" color="smMuted">
      <SearchHighlight text={displayPersonName(event.responsible)} term={searchedTerm} />
    </Text>,
    <Text key="contractType" color="smMuted">
      {event.contractNature
        ? getContractNatureLabel(event.contractNature, t)
        : "–"}
    </Text>,
    <Text key="deadline" color="smMuted">
      {event.deadline ? displayDateWithTime(event.deadline) : "–"}
    </Text>,
    <Text key="procedure" color="smMuted">
      {procedureText(event, t) ?? "–"}
    </Text>,
    <Text key="regulation" color="smMuted">
      {regulationText(event, t) ?? "–"}
    </Text>,
    <Text key="modifiedAt" color="smMuted">
      {displayDate(event.modifiedAt)}
    </Text>,
    ];
  });

  const reports = useListReports({
    list: SavedReportListDto.Procurements,
    query: reportQuery.toString(),
    heading,
    listFailedToLoad: isError,
    listIsLoading: isFetching,
    comparable: comparableReportQuery,
  });

  return (
    <Box>
      <ListPageHeader
        heading={reports.heading}
        headingColor="smText"
        mb={showsWholeList ? "2" : "6"}
        data-testid="procurement-list-header"
        actions={
          <>
            {reports.actions}
            <ProcurementListDownloadMenu
              count={page ? count : undefined}
              isLoading={isFetching}
              failed={isError}
              columns={columns
                .filter((column) => !column.hidden)
                .map(({ key, label }) => ({ key, label: String(label) }))}
              query={queryArg}
              filters={[
                ...(deadlinePassed ? [String(t("Past their deadline"))] : []),
                ...(searchedTerm ? [`${t("Search the table")}: «${searchedTerm}»`] : []),
                ...overviewChips.map((chip) => chip.label),
                ...describeColumnFilters(t, columns),
              ]}
            />
          </>
        }
      />
      {reports.notice}
      {showsWholeList && (
        <Text color="smMuted" mb="6">
          {t("All your sourcing events.")}
        </Text>
      )}

      <FilterBar
        columns={columns}
        extraChips={overviewChips.map((chip) => ({
          label: chip.label,
          onRemove: () => clearOverviewFilters(chip.keys),
        }))}
        defaultFilterColumnKeys={defaultFilterColumnKeys}
        offersHiddenColumns
        onResetAll={handleResetAllFilters}
        countText={countText}
        search={{
          value: searchInput,
          onChange: (value: string) => {
            setSearchInput(value);
            debouncedWriteSearch(value);
          },
          label: String(t("Search the table")),
          tooltip: String(t("Searches Title, Internal reference and Responsible person")),
        }}
      />
      {!!page?.notVisible && (
        <Text color="smMuted" fontSize="sm" mb="3" data-testid="procurements-not-visible">
          {t("{{count}} of these procurements are not shown, because you do not have access to them.", {
            count: page.notVisible,
          })}
        </Text>
      )}
      {isError ? (
        <Flex justify="center" py="16">
          <Text color="smError">{t("Could not load sourcing events.")}</Text>
        </Flex>
      ) : (
        <Box
          bg="smBackground"
          rounded="xl"
          border="1px solid"
          borderColor="smBorder"
          p="4"
        >
          <EntityTable
            emptyText={t("No sourcing events yet.")}
            noMatchText={terms.noProcurementsMatchFilters}
            noMatchContent={
              searchedTerm ? (
                <TableSearchNoMatch
                  heading={t("No procurements contain «{{term}}»", { term: searchedTerm })}
                  searchedIn={t("The search looks in Title, Internal reference and Responsible person.")}
                  foundElsewhere={t(
                    "Status, deadlines, contract type, procedure and regulation are found with + Filter.",
                  )}
                  filtersNote={
                    activeFilterDescriptions.length > 0
                      ? t("The search only covers the procurements that match the filters: {{filters}}.", {
                          filters: activeFilterDescriptions.join(", "),
                        })
                      : null
                  }
                  onClearSearch={clearSearch}
                  onResetFilters={handleResetAllFilters}
                />
              ) : undefined
            }
            columns={columns}
            rows={rows}
            size="md"
            isLoading={isFetching}
            count={count}
            limit={PAGE_SIZE}
            skip={skip}
            setSkip={(next) =>
              updateParams((params) => params.set("skip", `${next}`))
            }
            onReset={handleReset}
            hasActiveFilters={hasActiveFilters}
            resetKey={resetKey}
            onShowColumn={onShowColumn}
            onHideColumn={onHideColumn}
            onShowAllColumns={setVisibleColumns}
            onResetColumns={resetColumns}
            defaultFilterColumnKeys={defaultFilterColumnKeys}
            hasColumnChanges={hasColumnChanges}
          />
        </Box>
      )}
    </Box>
  );
};
